import { API_URL, FeatureFlags } from "@/constants";
import { CaretLeftIcon, CaretRightIcon, UserIcon } from "@phosphor-icons/react";
import { useCustomMutation, useInvalidate, useList } from "@refinedev/core";
import { Spin } from "antd";
import { CarouselProps } from "components/Carousel";
import { FatButton } from "components/FatButton/FatButton/";
import PageHeader from "components/PageHeader";
import { ShadowButton } from "components/ShadowButton";
import StructuredLayoutComponent from "components/StructuredLayoutComponent/";
import { useAnalytics } from "hooks/useAnalytics";
import { useFeature } from "hooks/useFeature";
import { t } from "i18next";
import { MicrophoneIcon } from "pages/media/components/ContactWithPresenter/icons";
import { SpeakerCarouselList } from "pages/media/components/SpeakerCarouselList";
import { ContactResponse, MediaProjectResponse } from "pages/media/types";
import { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { AnalyticsEvents } from "types/analytics";
import { AvatarDetails } from "../../components/ContactWithPresenter/AvatarDetails";
import ToolbarWizard from "./ToolbarWizard";
import { useSession } from "hooks/useSession/";

const SpeakerCarouselWrapper = styled.div`
  width: 95%;
  max-width: 1600px;
  .slick-slide {
    padding-inline: 12px;
    box-sizing: border-box;
  }
`;

export interface SpeakerSelectProps extends Omit<
  CarouselProps,
  "numberOfElements" | "children"
> {
  value?: ContactResponse["id"];
  onClick: (value: ContactResponse["id"]) => void;
  numberOfElements?: number;
  contacts: ContactResponse[];
  onEditContact?: (contact: ContactResponse) => void;
}

const SpeakerSelect: React.FC<SpeakerSelectProps> = (props) => {
  const sortedData = useMemo(
    () =>
      props.contacts.toSorted((a, b) => {
        const aName = a.firstname + a.lastname;
        const bName = b.firstname + b.lastname;
        return aName.localeCompare(bName);
      }),
    [props.contacts]
  );

  return (
    <SpeakerCarouselWrapper>
      <SpeakerCarouselList {...props} contacts={sortedData} />
    </SpeakerCarouselWrapper>
  );
};

const PRE_STEP = "PRE_STEP";
const CREATE_AVATAR = "CREATE_AVATAR";
const SELECT_AVATAR = "SELECT_AVATAR";

const _speakerSteps = [PRE_STEP, CREATE_AVATAR, SELECT_AVATAR] as const;

type SpeakerStep = (typeof _speakerSteps)[number];

interface SpeakerViewProps {
  previousStep?: () => void;
  project: MediaProjectResponse | null;
  nextStep?: () => void;
  initialStep?: SpeakerStep;
  showMoreFields: boolean;
  initialVoiceOnly: boolean;
  /**
   * Called when the user picks (or just created) a contact in "controlled" mode.
   * `isVoiceOnly` reflects whether the picked contact has no face profile so
   * the parent can keep `media_generation_mode` consistent with the contact.
   * `contact` is the picked contact (when resolvable) so parents don't need to
   * re-fetch the contacts list to look it up.
   */
  onContactChange?: (
    value: string,
    isVoiceOnly: boolean,
    contact?: ContactResponse
  ) => void;
  showNavigation?: boolean;
  showHeader?: boolean;
  itemsPerSlide?: number;
  scriptLanguage?: string;
  contentStyle?: React.CSSProperties;
  // Renders an edit button on each avatar card during SELECT_AVATAR — pass only from Video Editor contexts.
  onEditContact?: (contact: ContactResponse) => void;
}

const SpeakerView = ({
  previousStep,
  project,
  nextStep,
  initialStep,
  showMoreFields,
  onContactChange,
  showNavigation = true,
  showHeader = true,
  itemsPerSlide = 3,
  scriptLanguage = "",
  initialVoiceOnly = false,
  contentStyle,
  onEditContact,
}: SpeakerViewProps) => {
  const { isEnabled: isEnabledVoiceOnly } = useFeature(
    FeatureFlags.ContactFaceless
  );
  const { mutateAsync } = useCustomMutation();
  const { trackEvent } = useAnalytics();
  // TODO: WILL HAVE TO MAKE DYNAMIC WHEN WE HAVE BETTER INSIGHTS INTO HOW TO SELECT WHICH ITEM FROM MEDIA ARRAY
  const url = `${API_URL}/media/projects/${project?.id}/instances/${project?.media[0]?.instance_id}/settings`;
  const [voiceOnly, setVoiceOnly] = useState(initialVoiceOnly);
  const { currentOrganization: organization } = useSession();
  const invalidate = useInvalidate();

  const { data: contacts } = useList<ContactResponse>({
    resource: `media/${organization?.id}/contacts`,
    queryOptions: {
      enabled: !!organization?.id,
    },
  });

  const [selectedAvatar, setSelectedAvatar] = useState<string | undefined>(
    project?.settings.contact_id ?? undefined
  );

  const getInitialStep = (): SpeakerStep => {
    if (initialStep) return initialStep;
    if (project?.settings.contact_id) return SELECT_AVATAR;
    return PRE_STEP;
  };

  const [step, setStep] = useState<SpeakerStep>(getInitialStep);

  const [isVoiceOnlyStepComplete, setIsVoiceOnlyStepComplete] = useState(false);

  const updateContactSettings = useCallback(
    (contactId: string, isVoiceOnly: boolean) =>
      mutateAsync({
        url,
        method: "patch",
        values: {
          contact_id: contactId,
          // A contact without a face profile can only drive an audio experience.
          media_generation_mode: isVoiceOnly ? "Audio" : "Video",
        },
      }),
    [mutateAsync, url]
  );

  const trackContactUpdate = useCallback(
    (contactId: string) => {
      if (!project?.id) return;
      trackEvent(AnalyticsEvents.ProjectSettingUpdate, {
        project_id: project.id,
        setting_name: "contact",
        setting_value: JSON.stringify(contactId),
        page: "project_creation_wizard",
      });
    },
    [trackEvent, project]
  );

  const handleContactChange = useCallback(
    (contactId: string, isVoiceOnly?: boolean) => {
      if (project?.id) {
        void (async () => {
          // For the existing-contact path the caller doesn't know whether the
          // picked contact is voice-only, so derive it from the contacts list.
          const resolvedIsVoiceOnly =
            isVoiceOnly ??
            !contacts?.data.find((c) => c.id === contactId)?.presenter
              .face_profile;
          await updateContactSettings(contactId, resolvedIsVoiceOnly);
          trackContactUpdate(contactId);
          nextStep?.();
        })();
      }
    },
    [
      project?.id,
      contacts?.data,
      updateContactSettings,
      trackContactUpdate,
      nextStep,
    ]
  );

  const handleBackButtonClick = useCallback(() => {
    if (step === PRE_STEP) {
      previousStep?.();
    } else {
      setStep(PRE_STEP);
    }
  }, [step, previousStep]);

  const handleNextButtonClick = useCallback(() => {
    if (step === PRE_STEP) {
      setStep(CREATE_AVATAR);
    }
    if (step === SELECT_AVATAR && selectedAvatar) {
      handleContactChange(selectedAvatar);
    }
    if (step === CREATE_AVATAR) {
      void saveCallback.current?.();
    }
  }, [step, selectedAvatar, handleContactChange]);

  const saveCallback = useRef<() => Promise<void>>();

  const isNextButtonDisabled = useMemo(() => {
    return (
      (step === SELECT_AVATAR && !selectedAvatar) ||
      (step === CREATE_AVATAR && voiceOnly && !isVoiceOnlyStepComplete)
    );
  }, [step, selectedAvatar, voiceOnly, isVoiceOnlyStepComplete]);

  const getContent = useCallback(() => {
    switch (step) {
      case CREATE_AVATAR:
        return (
          <AvatarDetails
            voiceOnly={voiceOnly}
            scriptLanguage={scriptLanguage}
            onQRComplete={() => {
              if (voiceOnly) {
                invalidate({
                  resource: `media/${organization?.id}/voice_profiles`,
                  invalidates: ["list"],
                }).catch((error) =>
                  console.error("Error invalidating voice profiles", error)
                );
              } else {
                invalidate({
                  resource: `media/${organization?.id}/contacts`,
                  invalidates: ["list"],
                }).catch((error) =>
                  console.error("Error invalidating contacts", error)
                );
              }
              setStep(SELECT_AVATAR);
            }}
            saveCallback={saveCallback}
            style={{ width: "100%" }}
            setSubmitButtonEnabled={setIsVoiceOnlyStepComplete}
            onSuccess={(contactId: string) =>
              handleContactChange(contactId, voiceOnly)
            }
            isNewAvatar={true}
          />
        );
      case PRE_STEP:
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                gap: "64px",
              }}
            >
              {isEnabledVoiceOnly && (
                <div style={{ flex: "0 1 300px" }}>
                  <FatButton
                    size="large"
                    onClick={() => {
                      setVoiceOnly(true);
                      return setStep(CREATE_AVATAR);
                    }}
                    style={{
                      height: 350,
                      width: "100%",
                    }}
                    title={t(
                      "projects.components.ProjectCreateWizard.CreateVoiceOnlyContact.title"
                    )}
                    content={t(
                      "projects.components.ProjectCreateWizard.CreateVoiceOnlyContact.subtitle"
                    )}
                    icon={
                      <MicrophoneIcon
                        fillColor={"#fff"}
                        style={{
                          width: 36,
                          height: 36,
                        }}
                      />
                    }
                  />
                </div>
              )}
              <div
                style={{ flex: "0 1 300px" }}
                data-testid="use-existing-contact-button"
              >
                <FatButton
                  size="large"
                  onClick={() => setStep(SELECT_AVATAR)}
                  style={{
                    height: 350,
                    width: "100%",
                  }}
                  icon={<UserIcon size="30" />}
                  title={t(
                    "projects.components.ProjectCreateWizard.UseExistingContact.title"
                  )}
                  content={t(
                    "projects.components.ProjectCreateWizard.UseExistingContact.subtitle"
                  )}
                />
              </div>
              <div style={{ flex: "0 1 300px" }}>
                <FatButton
                  size="large"
                  onClick={() => {
                    setVoiceOnly(false);
                    return setStep(CREATE_AVATAR);
                  }}
                  style={{
                    height: 350,
                    width: "100%",
                  }}
                  title={t(
                    "projects.components.ProjectCreateWizard.CreateNewContact.title"
                  )}
                  content={t(
                    "projects.components.ProjectCreateWizard.CreateNewContact.subtitle"
                  )}
                  icon={<UserIcon size="30" />}
                />
              </div>
            </div>
          </div>
        );
      case SELECT_AVATAR:
        return (
          <SpeakerSelect
            value={selectedAvatar}
            onClick={(contactId) => {
              if (onContactChange) {
                const picked = contacts?.data.find((c) => c.id === contactId);
                onContactChange(
                  contactId,
                  !picked?.presenter.face_profile,
                  picked
                );
                setSelectedAvatar(contactId);
              } else {
                setSelectedAvatar(contactId);
              }
            }}
            slidesToShow={itemsPerSlide}
            contacts={contacts?.data || []}
            onEditContact={onEditContact}
          />
        );
      default:
        break;
    }
    // need to recompute when showMoreFields changes because it might affect the contacts list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    voiceOnly,
    showMoreFields,
    handleContactChange,
    scriptLanguage,
    isEnabledVoiceOnly,
    selectedAvatar,
    itemsPerSlide,
    contacts?.data,
    onContactChange,
    invalidate,
    organization?.id,
    onEditContact,
  ]);

  return (
    <div>
      <StructuredLayoutComponent
        header={
          showHeader &&
          (step === PRE_STEP || step === SELECT_AVATAR) && (
            <PageHeader
              title={t("projects.components.ProjectCreateWizard.Speaker")}
              subtitle={t(
                "projects.components.ProjectCreateWizard.SpeakerSubtitle"
              )}
            />
          )
        }
        navigation={
          showNavigation && (
            <ToolbarWizard>
              <ShadowButton
                type="default"
                shape="round"
                size="large"
                icon={<CaretLeftIcon />}
                onClick={handleBackButtonClick}
              >
                {t("buttons.back")}
              </ShadowButton>
              <ShadowButton
                type="primary"
                shape="round"
                size="large"
                icon={<CaretRightIcon />}
                iconPosition="end"
                onClick={handleNextButtonClick}
                disabled={isNextButtonDisabled}
              >
                {t("buttons.next")}
              </ShadowButton>
            </ToolbarWizard>
          )
        }
        contentStyle={contentStyle}
      >
        {getContent() ?? <Spin spinning />}
      </StructuredLayoutComponent>
    </div>
  );
};
export default SpeakerView;
